import React, { useEffect, useState, useCallback } from 'react';
import { Button, CircularProgress } from '@mui/material';
import DialogModal from '../../components/DialogModal';
import {
  DialogContent,
  DialogRow,
  AdminAccessSection,
  StyledTextField
} from './styled';
import { EditUserDialogProps } from './types';
import { UserStatus } from '../../types/userType';
import AdminAccessFields from './components/AdminAccessFields';
import { AdminAccess, SchoolOfficeUser } from '../../types/schoolCollection';
import { Gender, SnackbarSeverityType } from '../../enums';
import schema from './utils/useValidation';
import { validateForm } from './utils/validationHelpers';
import userApi from '../../apis/users';
import { DEFAULT_ACCESS } from './utils/utils';

const EditSchoolOfficeUserDialog: React.FC<EditUserDialogProps> = ({
  user,
  open,
  schoolCode,
  onClose,
  onUpdate,
  showToast
}) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    adminAccess: DEFAULT_ACCESS
  });
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        adminAccess: user.adminAccess || DEFAULT_ACCESS
      });
      setErrors({});
    } else if (!open) {
      setForm({ name: '', email: '', adminAccess: DEFAULT_ACCESS });
      setErrors({});
    }
  }, [user, open]);

  const handleFieldChange =
    (field: 'name' | 'email') => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const handleAdminAccessChange = (key: keyof AdminAccess, value: boolean) => {
    setForm((prev) => ({
      ...prev,
      adminAccess: {
        ...prev.adminAccess,
        ether: true,
        [key]: value
      }
    }));
  };

  const validate = useCallback(async () => {
    const { valid, errors: fieldErrors } = await validateForm(
      {
        name: form.name,
        email: form.email,
        gender: (user?.gender as Gender) || Gender.MALE
      },
      schema,
      ['name', 'email']
    );
    setErrors(fieldErrors);
    return valid;
  }, [form.name, form.email, user]);

  const handleUpdate = async () => {
    if (!user || !schoolCode) return;
    const isValid = await validate();
    if (!isValid) return;
    try {
      setIsUpdating(true);
      await userApi.updateUser(user.id, {
        name: form.name.trim(),
        email: form.email.trim()
      });
      const updatedUser: SchoolOfficeUser = {
        ...user,
        name: form.name.trim(),
        email: form.email.trim(),
        adminAccess: form.adminAccess
      };
      onUpdate(updatedUser);
      showToast('User updated successfully', SnackbarSeverityType.success);
      onClose();
    } catch (e) {
      console.error(e);
      showToast('Failed to update user', SnackbarSeverityType.error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    try {
      setIsUpdating(true);
      const newStatus =
        user.status === UserStatus.ACTIVE
          ? UserStatus.IN_ACTIVE
          : UserStatus.ACTIVE;
      await userApi.updateUserStatus(user.id, newStatus);
      const updatedUser: SchoolOfficeUser = { ...user, status: newStatus };
      onUpdate(updatedUser);
      showToast(
        `User ${
          newStatus === UserStatus.ACTIVE ? 'activated' : 'deactivated'
        } successfully`,
        SnackbarSeverityType.success
      );
      onClose();
    } catch (e) {
      console.error(e);
      showToast('Failed to update user status', SnackbarSeverityType.error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!user) return null;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Edit User'
      actions={
        <>
          <Button onClick={onClose} disabled={isUpdating} variant='contained'>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              'Update'
            )}
          </Button>
        </>
      }
    >
      <DialogContent>
        <DialogRow>
          <StyledTextField
            label='Name'
            size='small'
            value={form.name}
            onChange={handleFieldChange('name')}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <Button
            onClick={handleToggleStatus}
            disabled={isUpdating}
            variant='contained'
          >
            {user.status === UserStatus.ACTIVE
              ? 'Deactivate User'
              : 'Activate User'}
          </Button>
        </DialogRow>
        <StyledTextField
          label='Email'
          size='small'
          value={form.email}
          onChange={handleFieldChange('email')}
          error={Boolean(errors.email)}
          helperText={errors.email}
        />
        <AdminAccessSection>
          <AdminAccessFields
            adminAccess={form.adminAccess}
            onChange={handleAdminAccessChange}
          />
        </AdminAccessSection>
      </DialogContent>
    </DialogModal>
  );
};

export default EditSchoolOfficeUserDialog;
